/**
 * RemeshPipeline - Manages the chunk remesh queue and worker dispatch
 *
 * Responsibilities:
 * - Maintains a priority-sorted remesh queue
 * - Dispatches chunks to MeshWorkerPool (async) or meshes synchronously
 * - Applies worker results to ChunkGeometry + TerrainBatch
 * - Provides batch dispatch for atomic build operations
 *
 * Decoupled from VoxelWorld via callbacks for chunk/geometry lookup.
 */

import * as THREE from 'three';
import {
  CHUNK_WORLD_SIZE,
  POSITIVE_MARGIN_OFFSETS_7,
  chunkKeyLod,
  packChunkKeyLod,
} from '@worldify/shared';
import { Chunk, ChunkPhase } from './Chunk.js';
import { ChunkGeometry } from './ChunkGeometry.js';
import { TerrainBatch } from './TerrainBatch.js';
import { meshChunk, expandChunkToGrid, getSkipHighBoundary, getExtrapolatedMargins } from './ChunkMesher.js';
import { MarginFlightLedger } from './marginConvergence.js';
import { MeshWorkerPool, type MeshResult } from './MeshWorkerPool.js';
import { shellDistance } from './lodRequestSchedule.js';

// ---- Types ----

/** Callback fired when a chunk remesh result is applied */
export type RemeshListener = (chunkKey: string) => void;

// ============================================================
// RemeshPipeline
// ============================================================

/** Per-layer vertex/index counts of a worker result — what hasRoomFor sizes a reservation from. */
const ROOM_SLOTS: ({ verts: number; idx: number } | null)[] = [null, null, null];
function slotSizes(result: MeshResult): ReadonlyArray<{ verts: number; idx: number } | null> {
  const layers = [result.solid, result.transparent, result.liquid];
  for (let i = 0; i < 3; i++) {
    const d = layers[i];
    ROOM_SLOTS[i] = d ? { verts: d.positions.length / 3, idx: d.indices.length } : null;
  }
  return ROOM_SLOTS;
}

export class RemeshPipeline {
  /** Chunks that need remeshing */
  readonly queue = new Set<string>();

  /** Listeners notified when a remesh result is applied */
  private listeners = new Set<RemeshListener>();

  /** Reusable array for priority-sorted processing (avoids allocation) */
  private sortBuffer: string[] = [];

  /**
   * Dispatch is paced by the FRAME's clock (`process(..., budgetEnd)` — voxel/frameBudget.ts, one
   * deadline for every voxel consumer; this used to be a private count of 8 at ~0.5 ms each). At least
   * one dispatch always goes, so a saturated frame still makes progress.
   *
   * FAIRNESS, not budget: when both base (lod 0) and far-ring (lod > 0) work is queued, base may take
   * at most this many dispatches per frame — the rest of the clock is the rings', so the LOD swap
   * manager's "parent visible until children meshed" always makes progress during heavy base
   * streaming. (Rings sort after base, so with no ring work base uses the whole clock.)
   */
  private static readonly MAX_BASE_DISPATCHES_WITH_RINGS = 6;

  /** Cumulative count of mesh-worker dispatches — a churn gauge for streaming (surfaced in perf). */
  meshDispatches = 0;

  /**
   * Finished meshes waiting to be applied, and the per-frame cap on applying them.
   *
   * Worker results arrive in onmessage — BETWEEN frames, outside any budget. When the main thread is
   * busy (a long merge, a big walk) the messages queue up and then ALL apply in one frame: measured
   * a frame with `applies=222` at 0.064 ms each, ~14 ms of work that appeared in no PERF term
   * because it ran outside every timed section. Applying a mesh one frame later is invisible, so the
   * queue is drained against the frame's clock (voxel/frameBudget.ts — the first consumer on it, so
   * applies see the whole allowance) and the rest spills to the next frame.
   *
   * The chunk's PHASE is set at apply time together with its geometry, never separately: deferring
   * one without the other would let coverage believe a chunk is drawable while nothing is on screen
   * — the exact class of lie the ownership contract forbids.
   */
  private pendingApplies: Array<{ key: string; result: MeshResult; complete: boolean; extrapolated: number }> = [];
  /** Outstanding applies per key — counted, because one chunk can legitimately have two in flight. */
  private readonly pendingApplyKeys = new Map<string, number>();
  /** Applies performed by the last drainApplies() — surfaced for the PERF line. */
  lastAppliedCount = 0;
  /** Applies that waited a frame for their batch to make room (cumulative). */
  roomWaits = 0;

  /**
   * What the meshes currently between dispatch and apply had to extrapolate. Read by the arrival
   * re-trigger through `extrapolatedMarginsFor`, because the chunk's own mask describes the mesh ON
   * SCREEN and is therefore blind to the one being built — see marginConvergence.ts for the permanent
   * hairline cracks that blindness caused.
   */
  private readonly marginFlights = new MarginFlightLedger();


  // ---- Dependencies (injected) ----
  private readonly chunks: Map<string, Chunk>;
  private readonly geometries: Map<string, ChunkGeometry>;
  private readonly terrain: TerrainBatch;
  private readonly meshPool: MeshWorkerPool;
  private readonly pendingChunks: Set<string>;

  /**
   * True when the positive margin-source neighbour at (cx,cy,cz,lod) is EXPECTED to load but isn't
   * ready yet — meshing should wait for it so a chunk's border is built once with real data instead
   * of extrapolated-now / re-meshed-later. Injected by VoxelWorld (which knows loaded / pending /
   * reachable / margin-policy). Defaults to a pending-only check when omitted.
   */
  private readonly isMarginSourceExpected: (cx: number, cy: number, cz: number, lod: number) => boolean;

  /**
   * True when a chunk should be meshed NOW — i.e. it is on-screen or one ring inside the render
   * frontier (reachable ∪ face-ring, the same set the render gate draws). Chunks that are loaded only
   * to supply neighbour VOXELS as margin sources — the occluder shell, the underground bulk behind
   * rock the visibility BFS never reaches, and the LOD shells' sacrificial +margin ring — return
   * false: meshing them is pure waste (they never draw). They stay queued and mesh the moment the
   * player's view reaches them (process runs every frame).
   * Injected by VoxelWorld (which owns the reachable set); defaults to "mesh everything" when omitted.
   */
  private readonly shouldMeshNow: (cx: number, cy: number, cz: number, lod: number) => boolean;
  /**
   * THE FINE-FIRST / DECIMATION LADDER, in ONE place for every level. It used to live inside
   * VoxelWorld.shouldMeshChunk's lod>0 branch, but that predicate is also wired to `inRenderSet` and
   * feeds CoverageCore.baseChunkState — so a scheduling policy was riding on the function that
   * decides COVERAGE, and lod 0 could never be gated there at all (a base chunk reading "not in the
   * render set" classifies occluded, which counts as covered, which releases its coarse parent over
   * volume nothing draws: holes, not delay). Separate concern, separate gate.
   */
  private readonly levelAdmits: (lod: number) => boolean;

  constructor(
    chunks: Map<string, Chunk>,
    geometries: Map<string, ChunkGeometry>,
    terrain: TerrainBatch,
    meshPool: MeshWorkerPool,
    pendingChunks: Set<string>,
    isMarginSourceExpected?: (cx: number, cy: number, cz: number, lod: number) => boolean,
    shouldMeshNow?: (cx: number, cy: number, cz: number, lod: number) => boolean,
    levelAdmits?: (lod: number) => boolean,
    /** The packed-key twin of `geometries` (packChunkKeyLod), kept in step here and on unload — the
     *  stitcher's per-vertex lookups read it instead of building a key string. */
    private readonly geometryIndex?: Map<number, ChunkGeometry>,
  ) {
    this.chunks = chunks;
    this.geometries = geometries;
    this.terrain = terrain;
    this.meshPool = meshPool;
    this.pendingChunks = pendingChunks;
    this.isMarginSourceExpected = isMarginSourceExpected
      ?? ((cx, cy, cz, lod) => this.pendingChunks.has(chunkKeyLod(cx, cy, cz, lod)));
    this.shouldMeshNow = shouldMeshNow ?? (() => true);
    this.levelAdmits = levelAdmits ?? (() => true);
  }

  // ---- Listeners ----

  addListener(fn: RemeshListener): void { this.listeners.add(fn); }
  removeListener(fn: RemeshListener): void { this.listeners.delete(fn); }

  // ---- Queue management ----

  /**
   * True when the queue's contents changed since the last process() pass. Together with the
   * caller's `worldChanged` hint this gates the per-frame sort+scan: a settled world with a queue
   * full of PARKED entries (margin-source chunks deferred until reached) was paying a full sort —
   * with two chunks.get per comparison — every frame forever. Nothing can become dispatchable
   * without either a queue mutation (ingest → add) or a world change (BFS/visibility → the
   * shouldMeshNow / marginSourcesReady verdicts move), so skipping is exact, not heuristic.
   */
  private queueDirty = true;

  add(key: string): void {
    if (!this.queue.has(key)) {
      this.queue.add(key);
      this.queueDirty = true;
    }
  }
  delete(key: string): void {
    if (this.queue.delete(key)) this.queueDirty = true;
  }
  clear(): void { this.queue.clear(); this.queueDirty = true; }
  get size(): number { return this.queue.size; }

  /**
   * Why the last process() pass did work — 'idle' when it early-outed. Read by the audit's STEADY
   * line; see the assignment in process().
   */
  lastGate: 'idle' | 'worldChanged' | 'queueDirty' | 'playerMoved' | 'priorityKeys' = 'idle';

  /** Set by VoxelWorld to attribute dispatches per chunk in the audit (null = not instrumented). */
  onDispatch: ((key: string) => void) | null = null;

  /** Last player chunk process() ran for (distance ordering input — a cross re-sorts). */
  private lastPcx = NaN;
  private lastPcy = NaN;
  private lastPcz = NaN;

  // ---- Processing ----

  /**
   * Process the remesh queue by dispatching to workers.
   * Sorts by distance (nearest first), with priority keys at front.
   * Skips chunks already in-flight or with pending neighbors.
   * @param worldChanged Caller's signal that visibility/BFS/pending state moved this frame —
   *   see queueDirty. Pass true when unsure (the default keeps legacy behavior).
   */
  process(playerPos: THREE.Vector3, priorityKeys: ReadonlySet<string> | undefined, worldChanged: boolean, budgetEnd: number): void {
    this.lastGate = 'idle';
    if (this.queue.size === 0) return;

    const pcx = Math.floor(playerPos.x / CHUNK_WORLD_SIZE);
    const pcy = Math.floor(playerPos.y / CHUNK_WORLD_SIZE);
    const pcz = Math.floor(playerPos.z / CHUNK_WORLD_SIZE);
    const playerMoved = pcx !== this.lastPcx || pcy !== this.lastPcy || pcz !== this.lastPcz;
    if (!worldChanged && !this.queueDirty && !playerMoved && !(priorityKeys && priorityKeys.size > 0)) {
      return; // nothing can have become dispatchable since the last pass
    }
    // Which condition defeated the early-out. A steady 'remesh' cost with no dispatches behind it is
    // the gate being defeated every frame and paying the full sort (two chunks.get per comparison) —
    // indistinguishable from real meshing work in the perf panel.
    this.lastGate = worldChanged ? 'worldChanged'
      : this.queueDirty ? 'queueDirty'
      : playerMoved ? 'playerMoved'
      : 'priorityKeys';
    this.lastPcx = pcx; this.lastPcy = pcy; this.lastPcz = pcz;
    this.queueDirty = false;

    const sorted = this.sortBuffer;
    sorted.length = 0;
    for (const key of this.queue) sorted.push(key);

    const px = playerPos.x / CHUNK_WORLD_SIZE;
    const py = playerPos.y / CHUNK_WORLD_SIZE;
    const pz = playerPos.z / CHUNK_WORLD_SIZE;

    let hasRingWork = false;

    sorted.sort((a, b) => {
      // Priority chunks first
      if (priorityKeys) {
        const ap = priorityKeys.has(a) ? 0 : 1;
        const bp = priorityKeys.has(b) ? 0 : 1;
        if (ap !== bp) return ap - bp;
      }
      const ca = this.chunks.get(a);
      const cb = this.chunks.get(b);
      if (!ca || !cb) return ca ? -1 : cb ? 1 : 0;
      // FINEST FIRST, which also puts the whole base block ahead of every ring — the tail scan
      // below depends on that. Was COARSEST-first among rings, to keep a coarse parent available as
      // the fallback while its children meshed; the fine-first ladder and the decimation floor own
      // that sequencing now, and a second opinion here was the opposite one.
      if (ca.lod !== cb.lod) return ca.lod - cb.lod;
      // THE SAME KEY THE REQUESTER USED. This sorted on raw squared distance, which is not
      // shell-major: a shell-3 corner sits at 27 and a shell-4 face at 16, so the farther shell's
      // face meshed BEFORE the nearer shell's corner — the exact inversion shellDistance exists to
      // prevent, undone one stage after it was computed. Levels are equal by here, so the level's
      // own chunk units are well defined and this matches requestLodShellData's key exactly.
      const s = 1 << ca.lod;
      const da = shellDistance(ca.cx + 0.5 - px / s, ca.cy + 0.5 - py / s, ca.cz + 0.5 - pz / s);
      const db = shellDistance(cb.cx + 0.5 - px / s, cb.cy + 0.5 - py / s, cb.cz + 0.5 - pz / s);
      return da - db;
    });

    // Base sorts before rings, so any queued ring chunk sits at the tail — scan from the end, and
    // stop at the base block. DISPATCHABLE ring work only: a PARKED ring entry (deferred by
    // shouldMeshNow, which is where fine-first holds the rings back) can sit in the queue for the
    // whole cold load, and reserving slots against it just took two of the base's eight for nothing.
    for (let i = sorted.length - 1; i >= 0; i--) {
      const c = this.chunks.get(sorted[i]);
      if (!c) continue;
      if (c.lod === 0) break;
      if (this.levelAdmits(c.lod) && this.shouldMeshNow(c.cx, c.cy, c.cz, c.lod)
        && this.marginSourcesReady(c.cx, c.cy, c.cz, c.lod)) { hasRingWork = true; break; }
    }

    // With ring work queued, base takes at most MAX_BASE_DISPATCHES_WITH_RINGS dispatches so rings are
    // guaranteed forward progress (swap resolution depends on it); otherwise base uses the whole clock.
    const baseCap = hasRingWork ? RemeshPipeline.MAX_BASE_DISPATCHES_WITH_RINGS : Number.POSITIVE_INFINITY;

    let dispatched = 0;
    let baseDispatched = 0;
    let parked = 0;
    let scannedAll = true;
    for (let i = 0; i < sorted.length; ++i) {
      // The frame's clock, consulted between dispatches (each is a grid expansion, ~0.5 ms); at least one.
      if (dispatched > 0 && performance.now() >= budgetEnd) { scannedAll = false; break; }

      const key = sorted[i];
      const chunk = this.chunks.get(key);
      if (!chunk) { this.delete(key); continue; }
      if (chunk.lod === 0 && baseDispatched >= baseCap) continue;

      if (this.meshPool.isInFlight(key)) continue;
      if (this.meshPool.isPreviewChunk(key)) continue;
      // Don't spend a mesh-worker slot on a chunk that won't be drawn — the occluder shell / the
      // underground bulk behind rock. Left in the queue (not deleted): it meshes the frame the
      // player's view reaches it, since process() runs every frame and re-tests this.
      if (!this.levelAdmits(chunk.lod)) { parked++; continue; }
      if (!this.shouldMeshNow(chunk.cx, chunk.cy, chunk.cz, chunk.lod)) { parked++; continue; }
      if (!this.marginSourcesReady(chunk.cx, chunk.cy, chunk.cz, chunk.lod)) continue;

      const grid = this.meshPool.takeGrid();
      const skipHighBoundary = expandChunkToGrid(chunk, this.chunks, grid);
      const complete = !(skipHighBoundary[0] || skipHighBoundary[1] || skipHighBoundary[2]);
      // WHICH sources were extrapolated, captured at the same instant as the grid — the diagonals
      // are invisible to `complete` (a chunk missing only a diagonal is topologically whole and so
      // draws) yet their extrapolated edge/corner column is geometrically wrong until the real
      // source arrives. Travels with the geometry for the same reason the phase does.
      const extrapolated = getExtrapolatedMargins(chunk, this.chunks);
      // Visible to the arrival re-trigger from THIS INSTANT, not from the apply: the sources named
      // here are exactly the ones that may arrive while the worker runs.
      this.marginFlights.record(key, extrapolated);

      this.meshPool.dispatch(key, grid, skipHighBoundary, (result) => {
        // Queued, not applied: see pendingApplies. Phase travels WITH the geometry.
        this.pendingApplies.push({ key, result, complete, extrapolated });
        this.pendingApplyKeys.set(key, (this.pendingApplyKeys.get(key) ?? 0) + 1);
      }, chunk.lod);
      this.meshDispatches++;
      this.onDispatch?.(key);

      this.delete(key);
      dispatched++;
      if (chunk.lod === 0) baseDispatched++;
    }
    // Complete scans give the exact parked count; scans cut short by the dispatch cap only saw a
    // prefix, so keep the high-water mark instead of going stale-low (the overlay read "89 active"
    // when most of those were parked — the count had frozen at an early, busy scan).
    this.lastParkedCount = scannedAll ? parked : Math.max(this.lastParkedCount, parked);
  }

  /**
   * Queue entries deferred by shouldMeshNow at the last complete scan — chunks loaded only as
   * margin sources / occluder shell that will never mesh until the view reaches them. They sit in
   * the queue BY DESIGN and cost nothing per frame (the change-gate skips settled passes); split
   * out for the debug overlay so a large "remesh queue" reads as residency, not pending work.
   */
  private lastParkedCount = 0;
  get parkedCount(): number {
    return Math.min(this.lastParkedCount, this.queue.size);
  }

  /**
   * Apply finished meshes until the frame's clock runs out (at least one). Call once per frame BEFORE
   * the visibility walk so this frame's selection sees the geometry that just landed. Returns the
   * number applied.
   */
  drainApplies(budgetEnd: number): number {
    let n = 0;
    while (n < this.pendingApplies.length) {
      if (n > 0 && performance.now() >= budgetEnd) break;
      const { key, result, complete, extrapolated } = this.pendingApplies[n];
      // ROOM IS ASKED FOR, NEVER TAKEN, ON THIS PATH. An apply whose batch would have to compact or
      // grow to hold it stays queued (order kept) and lands next frame, after TerrainBatch.tick has
      // made the room on its own clock — see hasRoomFor for the 41 ms this used to cost.
      const chunk = this.chunks.get(key);
      if (chunk && !this.terrain.hasRoomFor(key, chunk.lod, slotSizes(result))) { this.roomWaits++; break; }
      n++;
      // Settle before setComplete, which bails when the chunk has been unloaded — the ledger entry
      // must go either way, or an evicted-and-reloaded key would carry a stale in-flight mask.
      this.marginFlights.settle(key);
      const left = (this.pendingApplyKeys.get(key) ?? 1) - 1;
      if (left > 0) this.pendingApplyKeys.set(key, left); else this.pendingApplyKeys.delete(key);
      this.setComplete(key, complete, extrapolated);
      this.applyResult(result);
    }
    if (n === this.pendingApplies.length) this.pendingApplies.length = 0;
    else this.pendingApplies.splice(0, n);
    this.lastAppliedCount = n;
    return n;
  }

  /** Finished meshes still waiting for a frame to apply them. */
  get pendingApplyCount(): number {
    return this.pendingApplies.length;
  }

  /**
   * Has this chunk a finished mesh waiting for a frame to apply it?
   *
   * NOT covered by `isBusy` (in flight, or queued-and-dispatchable) and NOT visible in the chunk's
   * PHASE, which only changes at apply — so between dispatch and apply a chunk reads as `Loaded`
   * with no mesh coming, and the residency invariant re-queues it. Measured on a cold load, that
   * dispatched a second identical mesh for nearly every coarse chunk (lod 1 1.57x, lod 2 1.93x —
   * ~41% of coarse mesh work) with nothing unconverged to justify it.
   */
  hasPendingApply(key: string): boolean {
    return this.pendingApplyKeys.has(key);
  }

  /**
   * Is any chunk at this LOD queued AND dispatchable — i.e. is there real mesh work left at that
   * level? PARKED and margin-waiting entries do not count: they sit in the queue by design and a
   * level whose whole queue is parked has nothing to wait for.
   *
   * Early-exits on the first hit, so the common "yes, plenty" answer is O(1)-ish; the full scan is
   * paid only when the level is genuinely drained, which is exactly when the answer must be exact.
   */
  // DELIBERATELY IGNORES levelAdmits: this answers "does this level still owe work", which is what
  // the ladder's completeness test reads. Filtering by the ladder here would make a gated level
  // report itself finished and unlatch the gate that is holding it.
  hasDispatchableAtLod(lod: number): boolean {
    for (const key of this.queue) {
      const c = this.chunks.get(key);
      if (!c || c.lod !== lod) continue;
      if (this.meshPool.isInFlight(key)) return true;
      if (this.shouldMeshNow(c.cx, c.cy, c.cz, c.lod)
        && this.marginSourcesReady(c.cx, c.cy, c.cz, c.lod)) return true;
    }
    return false;
  }

  /**
   * Remesh a single chunk synchronously (fallback / immediate path).
   */
  remeshSync(chunk: Chunk): void {
    const key = chunk.key;

    let geo = this.geometries.get(key);
    if (!geo) {
      geo = new ChunkGeometry(chunk);
      this.geometries.set(key, geo);
      this.geometryIndex?.set(packChunkKeyLod(chunk.cx, chunk.cy, chunk.cz, chunk.lod), geo);
    }

    const skip = getSkipHighBoundary(chunk, this.chunks);
    this.setComplete(key, !(skip[0] || skip[1] || skip[2]), getExtrapolatedMargins(chunk, this.chunks));
    const output = meshChunk(chunk, this.chunks);
    geo.updateFromSurfaceNet(output);
    this.registerWithTerrain(key, chunk, geo);
    chunk.clearDirty();
  }

  /**
   * Remesh all dirty chunks synchronously.
   */
  remeshAllDirty(): void {
    for (const chunk of this.chunks.values()) {
      if (chunk.dirty) this.remeshSync(chunk);
    }
    this.queue.clear();
  }

  /**
   * Dispatch a set of chunks as an atomic batch (for build operations).
   * All mesh results are applied together in one frame.
   */
  dispatchBatch(keys: Set<string>): void {
    const batchItems: Array<{
      chunkKey: string;
      grid: Uint32Array;
      skipHighBoundary: [boolean, boolean, boolean];
      extrapolated: number;
    }> = [];

    for (const key of keys) {
      const chunk = this.chunks.get(key);
      if (!chunk) continue;
      this.delete(key);
      const grid = this.meshPool.takeGrid();
      const skipHighBoundary = expandChunkToGrid(chunk, this.chunks, grid);
      // The mask travels with a batch mesh for the same reason it travels with a single one: a build
      // edit re-meshes with whatever is resident, and passing no mask here CLEARED the record of an
      // extrapolated diagonal — turning an editable chunk's healable crack into a permanent one.
      const extrapolated = getExtrapolatedMargins(chunk, this.chunks);
      this.marginFlights.record(key, extrapolated);
      batchItems.push({ chunkKey: key, grid, skipHighBoundary, extrapolated });
    }

    if (batchItems.length === 0) return;

    this.meshPool.dispatchBatch(batchItems, (results) => {
      for (const result of results) {
        const item = batchItems.find((b) => b.chunkKey === result.chunkKey);
        if (item) {
          const s = item.skipHighBoundary;
          this.marginFlights.settle(result.chunkKey);
          this.setComplete(result.chunkKey, !(s[0] || s[1] || s[2]), item.extrapolated);
        }
        this.applyResult(result);
      }
    });
  }

  /**
   * Check if a chunk key is busy — its mesh is genuinely about to change (in flight on a worker,
   * or queued AND currently dispatchable). PARKED queue entries — deferred until the view reaches
   * them (shouldMeshNow) or their margins arrive (marginSourcesReady) — do NOT count: they can sit
   * in the queue indefinitely by design, and treating them as busy blocked their neighbours' work
   * forever (frontier groups always contain a few — the standalone-mesh pile-up).
   */
  isBusy(key: string): boolean {
    if (this.meshPool.isInFlight(key)) return true;
    if (!this.queue.has(key)) return false;
    const chunk = this.chunks.get(key);
    if (!chunk) return false;
    return this.shouldMeshNow(chunk.cx, chunk.cy, chunk.cz, chunk.lod)
      && this.marginSourcesReady(chunk.cx, chunk.cy, chunk.cz, chunk.lod);
  }

  /**
   * True unless this chunk's applied mesh skipped a high boundary (absent + margin neighbour). A
   * never-meshed chunk reports complete — it has no geometry, so the render pass skips it anyway; the
   * flag only matters once a mesh exists. Consumed by the render gate to keep holed meshes off screen.
   * Source of truth is the chunk's own `phase` (dies with the chunk — no forget-on-unload contract).
   */
  isMeshComplete(key: string): boolean {
    const chunk = this.chunks.get(key);
    return !chunk || chunk.phase !== ChunkPhase.MeshedIncomplete;
  }

  /**
   * Which high-side margin sources this chunk is STILL waiting on: the ones its applied mesh
   * extrapolated, plus the ones any mesh of it now in flight extrapolated. THE input to the arrival
   * re-trigger — `Chunk.extrapolatedMargins` alone describes only what is on screen, and a source
   * arriving during a dispatch would slip through unnoticed and never be asked for again
   * (marginConvergence.ts). Union rather than replacement: a bit set for a mesh whose source has
   * since arrived can only cost a redundant re-mesh, while a missing bit costs a permanent crack.
   */
  extrapolatedMarginsFor(key: string): number {
    return this.marginFlights.unconvergedFor(key, this.chunks.get(key)?.extrapolatedMargins ?? 0);
  }

  /** Chunks with a mesh between dispatch and apply — a gauge for the audit. */
  get inFlightMarginCount(): number {
    return this.marginFlights.size;
  }

  /**
   * Record what the just-applied mesh was built from: whether every high FACE was built (the
   * phase — an unbuilt face is a hole, so such a mesh never draws) and which of the 7 high-side
   * sources were EXTRAPOLATED (the mask — a missing diagonal leaves the mesh drawable but wrong
   * along one edge/corner column, which the phase cannot express). Both must be set together, or
   * the arrival re-triggers read a mask that describes a previous mesh.
   */
  private setComplete(key: string, complete: boolean, extrapolated: number = 0): void {
    const chunk = this.chunks.get(key);
    if (!chunk) return;
    chunk.phase = complete ? ChunkPhase.MeshedComplete : ChunkPhase.MeshedIncomplete;
    chunk.extrapolatedMargins = extrapolated;
  }

  // ---- Private ----

  /** Apply a worker result: update geometry, copy it into the terrain batch, notify listeners. */
  private applyResult(result: MeshResult): void {
    const { chunkKey: key, solid, transparent, liquid } = result;
    const chunk = this.chunks.get(key);
    if (!chunk) return;

    let geo = this.geometries.get(key);
    if (!geo) {
      geo = new ChunkGeometry(chunk);
      this.geometries.set(key, geo);
      this.geometryIndex?.set(packChunkKeyLod(chunk.cx, chunk.cy, chunk.cz, chunk.lod), geo);
    }

    geo.updateFromData(solid, transparent, liquid);
    chunk.clearDirty();
    this.registerWithTerrain(key, chunk, geo);

    for (const listener of this.listeners) listener(key);
  }

  /** Copy the chunk's layer geometries into their (lod, layer) batches — what puts a mesh on screen. */
  private registerWithTerrain(key: string, chunk: Chunk, geo: ChunkGeometry): void {
    const worldPos = chunk.getWorldPosition();
    this.terrain.updateChunk(key, chunk.cx, chunk.cy, chunk.cz, geo.getGeometries(), worldPos, chunk.lod);
  }

  /**
   * True once every positive margin source is resolved — loaded, or confirmed not-coming — so the
   * chunk can mesh its high-side border ONCE with real neighbour data. A mesh's high-side margin is
   * filled from all 7 positive neighbours (+X/+Y/+Z faces, edges, corner — see expandChunkData); we
   * defer while any is still EXPECTED to load (pending or reachable-but-unloaded, per
   * isMarginSourceExpected), which turns "mesh extrapolated now → re-mesh when the neighbour arrives"
   * into a single mesh. The dependency only points +XYZ, so it terminates at the frontier (no cycles):
   * the outermost chunks — whose + neighbours aren't coming — mesh first and readiness cascades inward.
   */
  private marginSourcesReady(cx: number, cy: number, cz: number, lod: number): boolean {
    for (const [dx, dy, dz] of POSITIVE_MARGIN_OFFSETS_7) {
      if (this.isMarginSourceExpected(cx + dx, cy + dy, cz + dz, lod)) return false;
    }
    return true;
  }
}
